import { styled } from '@mui/material/styles';
import { Card, Button, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';

// Nordic color palette
export const colors = {
  nordicWhite: '#fefffe',
  iceBlue: '#e8f4f8',
  fjordBlue: '#2c5f7a',
  midnightBlue: '#1a3344',
  auroraGreen: '#4a9b8d',
  sunsetOrange: '#d4821a',
  forestGreen: '#2d5016',
  shadowBlue: 'rgba(44, 95, 122, 0.15)',
  textPrimary: '#1a3344',
  textSecondary: '#2c5f7a',
};

// Styled components
export const DashboardContainer = styled(Box)(({ theme }) => ({
  maxWidth: '1400px',
  margin: '0 auto',
  padding: theme.spacing(3),
  animation: 'slideIn 0.8s ease-out',
  '@keyframes slideIn': {
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
  },
}));

export const DashboardHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-20px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '4px',
    borderRadius: '2px',
  },
  '& h1': {
    fontSize: '3rem',
    fontWeight: 300,
    color: colors.midnightBlue,
    marginBottom: theme.spacing(1),
    letterSpacing: '-0.02em',
    [theme.breakpoints.down('md')]: {
      fontSize: '2rem',
    },
  },
  '& p': {
    color: colors.textSecondary,
    fontSize: '1.1rem',
    fontWeight: 400,
  },
}));

export const StyledCard = styled(Card)(({ theme }) => ({
  background: colors.nordicWhite,
  padding: theme.spacing(3),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'visible',
}));

export const StatCardStyled = styled(StyledCard)({
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    borderRadius: '16px 16px 0 0',
  },
});

export const StatNumber = styled(Box)({
  fontSize: '2.5rem',
  fontWeight: 600,
  color: colors.midnightBlue,
  marginBottom: '0.5rem',
});

export const StatLabel = styled(Box)({
  color: colors.textSecondary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontSize: '0.9rem',
  fontWeight: 500,
});

export const ReleaseCardStyled = styled(Box)(({ theme }) => ({
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: 'fadeInUp 0.6s ease-out backwards',
  '@keyframes fadeInUp': {
    from: {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

export const ReleaseTitle = styled(Box)({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: colors.midnightBlue,
  marginBottom: '0.25rem',
});

export const ReleaseArtist = styled(Box)({
  color: colors.textSecondary,
  fontSize: '0.95rem',
});

export const ReleaseLabel = styled(Box)({
  color: colors.textSecondary,
  fontSize: '0.95rem',
});

export const ReleaseDetails = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  color: colors.textSecondary,
  flexWrap: 'wrap',
}));

export const StatusBadge = styled(Box)(({ status }) => {
  const styles = {
    pending: {
      background: 'rgba(212, 130, 26, 0.1)',
      color: colors.sunsetOrange,
    },
    submitted: {
      background: 'rgba(74, 155, 141, 0.1)',
      color: colors.auroraGreen,
    },
    focus: {
      background: 'rgba(45, 80, 22, 0.1)',
      color: colors.forestGreen,
      fontWeight: 600,
    },
    external: {
      background: 'rgba(45, 80, 22, 0.1)',
      color: colors.forestGreen,
      fontWeight: 600,
    },
    website: {
      background: 'rgba(45, 22, 80, 0.1)',
      color: '#330033',
      fontWeight: 600,
    },
  };

  return {
    padding: '0.25rem 0.75rem',
    fontSize: '0.8rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    ...styles[status],
  };
});

export const FocusCardStyled = styled(StyledCard)({ 
});

export const FocusTitle = styled(Box)(({ theme }) => ({
  color: colors.forestGreen,
  fontSize: '1.2rem',
  fontWeight: 600,
  marginBottom: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const FocusCount = styled(Box)({
  background: colors.forestGreen,
  color: colors.nordicWhite,
  padding: '0.25rem 0.5rem',
  fontSize: '0.8rem',
});

export const ActionButton = styled(LoadingButton)(({ theme }) => ({
  width: '100%',
}));

export const FilterTab = styled(Button)(({ active }) => ({
  padding: '0.5rem 1rem', 
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  fontSize: '0.9rem',
  textTransform: 'none',
  minWidth: 'auto',
}));

export const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(1.5),
  borderBottom: `2px solid ${colors.iceBlue}`,
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

export const SectionTitle = styled(Box)({
  fontSize: '1.5rem',
  fontWeight: 500,
  color: colors.midnightBlue,
});
